import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ValidationService } from '@app/forms/validation.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { mgcIconDownload2Regular } from '@sb/mgc-icons/mgcIcon-download_2_regular.icon';
import { featureFlag, FeatureFlagPipe, FeatureService } from '@sb/shared/feature';
import { ButtonComponent, IconButtonComponent } from '@sb/ui/button';
import { SbDialogModule, SbDialogService } from '@sb/ui/dialog';
import { SbFormFieldComponent } from '@sb/ui/form-field';
import { IconComponent, IconRegistry } from '@sb/ui/icon';
import { InputDirective } from '@sb/ui/input';
import { NoticeActionDirective, NoticeComponent } from '@sb/ui/notice';
import { PdfViewerComponent, type PdfViewerHiddenControls } from '@sb/ui/pdf-viewer';
import { SpinnerComponent } from '@sb/ui/spinner';
import { ToastService } from '@sb/ui/toast';
import { filter, take, tap } from 'rxjs';

import { templateContractStore } from '../../contract-template.store';
import { toTranslatedESignField, type ESignField } from '../../models/contract-template.model';
import { ContractTemplateUploadDialog } from '../../services/contract-template-upload-dialog';
import { ContractTemplateService } from '../../services/contract-template.service';
import { EditTemplateContractModalComponent } from '../edit-contract-template-modal/edit-contract-template-modal.component';
import { FieldsButtonComponent } from '../fields-button/fields-button.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'preview-template-contract-modal',
  imports: [
    SbDialogModule,
    TranslatePipe,
    ButtonComponent,
    IconButtonComponent,
    IconComponent,
    FieldsButtonComponent,
    ReactiveFormsModule,
    SbFormFieldComponent,
    InputDirective,
    FeatureFlagPipe,
    PdfViewerComponent,
    SpinnerComponent,
    NoticeComponent,
    NoticeActionDirective,
  ],
  templateUrl: './preview-template-contract-modal.component.html',
  styleUrl: './preview-template-contract-modal.component.scss',
})
export class PreviewTemplateContractModalComponent {
  translateService = inject(TranslateService);
  toast = inject(ToastService);
  contractTemplateService = inject(ContractTemplateService);
  #uploadDialog = inject(ContractTemplateUploadDialog);
  #dialogRef = inject(DialogRef<boolean>);
  #dialogService = inject(SbDialogService);
  #templateContractStore = inject(templateContractStore);
  #data = inject(DIALOG_DATA);

  #pdfPreviewEnabled = inject(FeatureService).isEnabled(featureFlag.TMP_DOCUMENT_PDF_PREVIEW);

  templateId = this.#data.templateId;
  featureFlag = featureFlag;

  // The reader's bar would show the pdf title, which the docx-to-html step fills with a library name.
  hiddenPreviewControls: PdfViewerHiddenControls = { toolbar: true, navPanes: true, scrollbar: true };

  preview = rxResource({
    params: () => (this.#pdfPreviewEnabled ? { templateId: this.templateId } : undefined),
    stream: ({ params }) => this.contractTemplateService.getTemplatePreview(params.templateId),
  });

  templateContractInfo = this.#templateContractStore.getTemplate(this.templateId);

  missingESignFields = computed<ESignField[]>(() =>
    (this.templateContractInfo()?.missingESignFields ?? []).flatMap((placeholderGroup) =>
      placeholderGroup.map((placeholderName) => toTranslatedESignField(placeholderName, this.translateService)),
    ),
  );

  form = new FormGroup({
    title: new FormControl('', [Validators.required, ValidationService.nonEmptyValidator]),
  });

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconDownload2Regular]);

    this.contractTemplateService.loadTemplate(this.templateId);

    effect(() => {
      const info = this.templateContractInfo();
      if (info) {
        this.form.patchValue({ title: info.title });
      }
    });
  }

  close() {
    this.#dialogRef.close();
  }

  finish() {
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return;
    }

    void this.contractTemplateService
      .updateTemplateContractInfo(this.templateContractInfo().id, this.form.getRawValue())
      .pipe(take(1))
      .subscribe(() => {
        this.contractTemplateService.notifyTemplateCreated(this.templateId);
        this.close();
      });
  }

  openEditModal() {
    void this.#dialogService.setSize('sm').open(EditTemplateContractModalComponent, {
      data: { contractInfo: this.templateContractInfo() },
    });
  }

  downloadTemplate() {
    void this.contractTemplateService.downloadTemplateContract(this.templateId).pipe(take(1)).subscribe();
  }

  deleteUploadedDocument() {
    void this.#dialogService
      .openConfirm({
        title: this.translateService.instant('Delete contract template'),
        type: 'warning',
        description: this.translateService.instant(
          'Are you sure you want to delete this template? This action cannot be undone.',
        ),
        primary: {
          text: this.translateService.instant('Delete'),
        },
        secondary: {
          text: this.translateService.instant('Cancel'),
        },
      })
      .closed.pipe(
        filter((result) => result?.confirmed),
        tap(() => this.#deleteTemplateContract()),
        take(1),
      )
      .subscribe();
  }

  #deleteTemplateContract() {
    void this.contractTemplateService
      .deleteTemplateContract(this.templateId)
      .pipe(
        take(1),
        tap(() => {
          this.close();
          this.#uploadDialog.openManualUpload(this.#data);
        }),
      )
      .subscribe();
  }
}
